import classNames from 'classnames';
import {
  useCallback,
  useState,
  type FunctionComponent,
  type ReactElement,
} from 'react';

import { useFeatureGate } from '@trello/feature-gate-client';
import { navigate } from '@trello/router/navigate';

import { LazySingleAttachmentViewer } from 'app/src/components/AttachmentViewer';
import { useAttachmentViewerFragment } from './AttachmentViewerFragment.generated';
import { useActionContext } from './useActionContext';

import styles from './EntityWithAttachmentViewer.less';

interface EntityWithAttachmentViewerProps {
  actionId: string;
  attachmentId: string;
  children: ReactElement | string;
  cardId: string;
}
export const EntityWithAttachmentViewer: FunctionComponent<
  EntityWithAttachmentViewerProps
> = ({ actionId, attachmentId, children, cardId }) => {
  const [isAttachmentViewerOpen, setIsAttachmentViewerOpen] = useState(false);
  const { location } = useActionContext();
  const { data: attachment } = useAttachmentViewerFragment({
    from: {
      id: attachmentId,
    },
  });

  const { value: isMalwareScanningFeatureEnabled } = useFeatureGate(
    'phx_malware_scanning_for_attachments',
  );

  const isMalicious =
    attachment?.isMalicious && isMalwareScanningFeatureEnabled;

  const onCloseAttachmentViewer = useCallback(() => {
    setIsAttachmentViewerOpen(false);
  }, []);

  const onClickOpenAttachmentViewer = useCallback(() => {
    if (isMalicious) {
      return;
    }

    if (['board', 'member'].includes(location)) {
      navigate(`/c/${cardId}?attachmentId=${attachmentId}#action-${actionId}`, {
        trigger: true,
      });
    } else {
      setIsAttachmentViewerOpen(true);
    }
  }, [actionId, attachmentId, cardId, isMalicious, location]);

  const isAttachmentLink = typeof children === 'string' ? true : false;

  if (isMalicious && !isAttachmentLink) {
    return null;
  }

  return (
    <>
      <div
        className={classNames(isAttachmentLink && styles.attachmentLink, {
          [styles.maliciousAttachment]: isMalicious,
        })}
        role="button"
        onClick={onClickOpenAttachmentViewer}
      >
        {children}
      </div>
      {isAttachmentViewerOpen && (
        <LazySingleAttachmentViewer
          onClose={onCloseAttachmentViewer}
          attachmentId={attachmentId}
          cardId={cardId}
        />
      )}
    </>
  );
};
